import React from "react";
import {Button, Spinner} from "react-bootstrap";
import {Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {CheckCircle, ClipboardList, Clock, Download, Info} from "lucide-react";
import CustomToolTip from "components/CustomToolTip";
import toastr from "toastr";

const SLA_GREEN_THRESHOLD = 90;
const SLA_AMBER_THRESHOLD = 80;

const BAND_COLORS = {
    green: "#059454",
    yellow: "#f0ad4e",
    orange: "#fd7e14",
    red: "#dc3545",
    neutral: "#4178be",
};

function businessDayBandColor(days: number): string {
    if (days <= 3) return BAND_COLORS.green;
    if (days <= 7) return BAND_COLORS.yellow;
    if (days <= 10) return BAND_COLORS.orange;
    return BAND_COLORS.red;
}

function headlineColor(pct: number): string {
    if (pct >= SLA_GREEN_THRESHOLD) return BAND_COLORS.green;
    if (pct >= SLA_AMBER_THRESHOLD) return BAND_COLORS.yellow;
    return BAND_COLORS.red;
}

export default class AdjudicationSlaReport extends React.Component<any, any> {
    state = {
        loading: true,
        report: null as any,
        showTables: true,
        selectedPeriod: "ytd",
    };

    componentDidMount() {
        fetch("/api/opsreport/getadjudicationsla", {method: "GET"})
            .then((response) => response.json())
            .then((data) => {
                const latest = data && data.months && data.months.length > 0 ? data.months[0].key : "ytd";
                this.setState({loading: false, report: data, selectedPeriod: latest});
            })
            .catch(() => {
                this.setState({loading: false, report: null});
            });
    }

    downloadCsv = async (file: string) => {
        const {selectedPeriod} = this.state;
        try {
            const response = await fetch(
                `/api/opsreport/downloadadjudicationsla?file=${file}&period=${encodeURIComponent(selectedPeriod)}`);
            if (!response.ok) {
                throw new Error("HTTP error " + response.status);
            }
            const blob = await response.blob();
            const periodSlug = selectedPeriod === "ytd" ? "YTD" : selectedPeriod;
            const fileName = `AdjudicationSla-${file}-${periodSlug}.csv`;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download SLA CSV:", error);
            toastr.error("Failed to download the CSV. Please try again.");
        }
    };

    renderDistributionTable(title: string, distribution: any[], colorBands: boolean, turnaroundDesc: string) {
        return (
            <div className="col-md-6 col-sm-12 d-flex flex-column">
                <div className="dashboard-summary-header mt-3" style={{fontSize: 15}}>
                    {title}
                    <CustomToolTip header={title} bodyContent={turnaroundDesc}/>
                </div>
                <div className="dashboard-table-container flex-grow-1">
                    <table className="table table-striped table-hover">
                        <thead>
                            <tr>
                                <th>Turnaround (days)</th>
                                <th>Count of Claims</th>
                                <th>
                                    % of Claims
                                    <CustomToolTip header="% of Claims"
                                                   bodyContent="Share of the selected period's adjudicated claims that fall at this turnaround value."/>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {distribution.map((bucket) => (
                                <tr key={bucket.days}>
                                    <td style={colorBands ? {
                                        color: businessDayBandColor(bucket.days),
                                        fontWeight: 600,
                                    } : undefined}>
                                        {bucket.days}
                                    </td>
                                    <td>{bucket.count.toLocaleString()}</td>
                                    <td>{bucket.pct.toFixed(2)}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    renderHistogram(title: string, distribution: any[], colorBands: boolean, turnaroundDesc: string) {
        return (
            <div className="col-md-6 col-sm-12">
                <div className="dashboard-summary-header mt-3" style={{fontSize: 15}}>
                    {title}
                    <CustomToolTip header={title} bodyContent={turnaroundDesc}/>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                        data={distribution}
                        margin={{top: 20, right: 30, left: 20, bottom: 5}}
                    >
                        <CartesianGrid strokeDasharray="3 3"/>
                        <XAxis dataKey="days"/>
                        <YAxis/>
                        <Tooltip formatter={(value: any, _name: any, entry: any) =>
                            [`${value.toLocaleString()} claims (${entry.payload.pct.toFixed(2)}%)`, "Claims"]
                        }/>
                        <Bar dataKey="count">
                            {distribution.map((bucket) => (
                                <Cell
                                    key={bucket.days}
                                    fill={colorBands ? businessDayBandColor(bucket.days) : BAND_COLORS.neutral}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    }

    render() {
        const {loading, report, showTables, selectedPeriod} = this.state;

        if (loading) {
            return <Spinner animation="border"/>;
        }

        if (!report || !report.available) {
            return (
                <div className="text-muted mt-4">
                    <Info size={16} style={{marginRight: 6}}/>
                    SLA snapshot not available yet. The snapshot is generated daily by the
                    AdjudicationSlaSnapshot job — check back after the next run.
                </div>
            );
        }

        const {ytd, months, snapshotAtUtc} = report;
        const snapshotLocal = snapshotAtUtc ? new Date(snapshotAtUtc).toLocaleString() : "—";
        const selectedMonth = months.find((m: any) => m.key === selectedPeriod);
        const period = selectedMonth || ytd;
        const periodLabel = selectedMonth ? selectedMonth.label : "Year to date";
        const summary = period.summary;
        const businessDayDistribution = period.businessDayDistribution;
        const calendarDayDistribution = period.calendarDayDistribution;

        return (
            <>
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                    <div className="dashboard-summary-header">
                        Adjudication SLA — Turnaround Time
                        <div className="text-muted" style={{fontSize: 13, fontWeight: 400}}>
                            Snapshot as of {snapshotLocal} · refreshes daily
                        </div>
                    </div>
                    <div className="d-flex align-items-center">
                        <select className="form-select form-select-sm me-2"
                                style={{width: "auto"}}
                                value={selectedPeriod}
                                onChange={(e) => this.setState({selectedPeriod: e.target.value})}>
                            <option value="ytd">Year to date</option>
                            {months.map((m: any) => (
                                <option key={m.key} value={m.key}>{m.label}</option>
                            ))}
                        </select>
                        <Button variant="outline-primary" size="sm" className="me-2"
                                onClick={() => this.downloadCsv("summary")}>
                            <Download size={14} style={{marginRight: 4}}/>
                            Summary CSV
                        </Button>
                        <Button variant="outline-primary" size="sm"
                                onClick={() => this.downloadCsv("detail")}>
                            <Download size={14} style={{marginRight: 4}}/>
                            Detail CSV
                        </Button>
                    </div>
                </div>

                <div className="text-muted mt-2" style={{fontSize: 13}}>
                    Showing: <strong>{periodLabel}</strong>
                </div>

                <div className="row mt-4">
                    <div className="col-md-3 col-sm-12">
                        <div className="dashboard-summary-card">
                            <div className="dashboard-card-icon">
                                <ClipboardList size={14}/> TOTAL
                            </div>
                            <div className="dashboard-summary-value">
                                {summary.totalClaims.toLocaleString()}
                            </div>
                            <div>Adjudicated Claims</div>
                        </div>
                    </div>
                    <div className="col-md-3 col-sm-12">
                        <div className="dashboard-summary-card">
                            <div className="dashboard-card-icon">
                                <CheckCircle size={14}/> WITHIN 3 DAYS
                            </div>
                            <div className="dashboard-summary-value"
                                 style={{color: headlineColor(summary.pctWithin3Days)}}>
                                {summary.pctWithin3Days.toFixed(2)}%
                            </div>
                            <div>{summary.within3BusinessDays.toLocaleString()} claims in ≤3 business days</div>
                        </div>
                    </div>
                    <div className="col-md-3 col-sm-12">
                        <div className="dashboard-summary-card">
                            <div className="dashboard-card-icon">
                                <Clock size={14}/> 4–10 DAYS
                            </div>
                            <div className="dashboard-summary-value">
                                {(summary.days4To7 + summary.days8To10).toLocaleString()}
                            </div>
                            <div>{summary.days4To7.toLocaleString()} in 4–7 · {summary.days8To10.toLocaleString()} in 8–10</div>
                        </div>
                    </div>
                    <div className="col-md-3 col-sm-12">
                        <div className="dashboard-summary-card">
                            <div className="dashboard-card-icon">
                                <Clock size={14}/> 11+ DAYS
                            </div>
                            <div className="dashboard-summary-value"
                                 style={{color: summary.days11Plus > 0 ? BAND_COLORS.red : undefined}}>
                                {summary.days11Plus.toLocaleString()}
                            </div>
                            <div>Claims over 10 business days</div>
                        </div>
                    </div>
                </div>

                <div className="row mt-4">
                    {this.renderHistogram("Business Days Turnaround", businessDayDistribution, true,
                        "Number of business days (weekends excluded) between the date a claim was received and the date it was adjudicated.")}
                    {this.renderHistogram("Calendar Days Turnaround", calendarDayDistribution, false,
                        "Number of calendar days (including weekends) between the date a claim was received and the date it was adjudicated.")}
                </div>

                <div className="mt-4">
                    <Button variant="outline-secondary" size="sm"
                            onClick={() => this.setState({showTables: !showTables})}>
                        {showTables ? "Hide" : "Show"} distribution tables
                    </Button>
                    {showTables && (
                        <div className="row">
                            {this.renderDistributionTable("Business Days Turnaround", businessDayDistribution, true,
                                "Number of business days (weekends excluded) between the date a claim was received and the date it was adjudicated.")}
                            {this.renderDistributionTable("Calendar Days Turnaround", calendarDayDistribution, false,
                                "Number of calendar days (including weekends) between the date a claim was received and the date it was adjudicated.")}
                        </div>
                    )}
                </div>
            </>
        );
    }
}
